import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Search,
  Plus,
  CheckCircle,
  Upload,
  ExternalLink,
  AlertCircle,
} from 'lucide-react'
import { useOfficeDocument } from '../../hooks/useOfficeDocument'
import { useTemplatePlaceholders, sanitizeVariableName } from '../../hooks/useTemplatePlaceholders'
import { useCreateTemplate, batchUpdateVariables } from '../../hooks/useTemplates'
import { VariableConfigCard } from './VariableConfigCard'
import type { LocalVariableConfig } from './VariableConfigCard'
import type { TemplateCategory, TemplateVariable } from '../../services/templatesApi'
import { MAIN_APP_URL } from '../../config'

type WizardStep = 'variables' | 'configure' | 'metadata' | 'success'

const CATEGORIES: Array<{ value: TemplateCategory; labelKey: string }> = [
  { value: 'hr', labelKey: 'templates.categories.hr' },
  { value: 'sales', labelKey: 'templates.categories.sales' },
  { value: 'legal', labelKey: 'templates.categories.legal' },
  { value: 'real_estate', labelKey: 'templates.categories.realEstate' },
  { value: 'finance', labelKey: 'templates.categories.finance' },
  { value: 'internal', labelKey: 'templates.categories.internal' },
  { value: 'other', labelKey: 'templates.categories.other' },
]

interface CreateTemplateWizardProps {
  onBack: () => void
  organizationId?: string
}

function toTitleCase(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * 4-step wizard for creating templates with deep Word integration.
 *
 * Step 1: Insert & scan {{variables}} in the Word document
 * Step 2: Configure each variable (label, type, required)
 * Step 3: Template metadata (name, description, category)
 * Step 4: Success
 */
export function CreateTemplateWizard({ onBack, organizationId }: CreateTemplateWizardProps) {
  const { t } = useTranslation()
  const { getDocumentBlob, isReading } = useOfficeDocument()
  const { insertPlaceholder, scanPlaceholders, isInserting, isScanning } = useTemplatePlaceholders()
  const createTemplate = useCreateTemplate()

  // Wizard state
  const [step, setStep] = useState<WizardStep>('variables')

  // Step 1: Variables
  const [detectedNames, setDetectedNames] = useState<string[]>([])
  const [newVarName, setNewVarName] = useState('')
  const [insertError, setInsertError] = useState<string | null>(null)

  // Step 2: Configure
  const [variableConfigs, setVariableConfigs] = useState<Record<string, LocalVariableConfig>>({})

  // Step 3: Metadata
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<TemplateCategory | ''>('')

  // Step 3-4: Upload state
  const [uploadPhase, setUploadPhase] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [createdTemplateId, setCreatedTemplateId] = useState<number | null>(null)
  const [createdVariables, setCreatedVariables] = useState<TemplateVariable[]>([])

  // Auto-scan on mount
  useEffect(() => {
    handleScan()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleScan = useCallback(async () => {
    const names = await scanPlaceholders()
    setDetectedNames(names)

    // Initialize configs for new variables
    setVariableConfigs((prev) => {
      const updated = { ...prev }
      for (const n of names) {
        if (!updated[n]) {
          updated[n] = {
            label: toTitleCase(n),
            field_type: 'text',
            is_required: true,
          }
        }
      }
      return updated
    })
  }, [scanPlaceholders])

  const handleInsert = async () => {
    const sanitized = sanitizeVariableName(newVarName)
    if (!sanitized) {
      setInsertError(t('wizard.invalidName'))
      return
    }
    if (detectedNames.includes(sanitized)) {
      setInsertError(t('wizard.duplicateName'))
      return
    }

    setInsertError(null)
    await insertPlaceholder(sanitized)
    setNewVarName('')

    // Re-scan after insert to update the list
    await handleScan()
  }

  const handleConfigChange = (varName: string, config: LocalVariableConfig) => {
    setVariableConfigs((prev) => ({ ...prev, [varName]: config }))
  }

  const handleCreate = async () => {
    setUploadError(null)

    try {
      // Phase 1: Read document
      setUploadPhase(t('wizard.readingDocument'))
      const blob = await getDocumentBlob()
      const filename = `${name.trim()}.docx`

      // Phase 2: Upload
      setUploadPhase(t('wizard.uploading'))
      const result = await createTemplate.mutateAsync({
        file: blob,
        filename,
        name: name.trim(),
        description: description.trim() || undefined,
        category: category || undefined,
        organizationId,
      })

      setCreatedTemplateId(result.template.id)
      setCreatedVariables(result.variables.variables)

      // Phase 3: Configure variables
      if (result.variables.variables.length > 0 && Object.keys(variableConfigs).length > 0) {
        setUploadPhase(t('wizard.configuring'))
        await batchUpdateVariables(
          result.template.id,
          result.variables.variables,
          variableConfigs,
          organizationId,
        )
      }

      setUploadPhase(null)
      setStep('success')
    } catch (err) {
      setUploadPhase(null)
      setUploadError(err instanceof Error ? err.message : t('common.error'))
    }
  }

  const stepIndex = { variables: 0, configure: 1, metadata: 2, success: 3 }[step]

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-200 dark:border-neutral-700">
        <button
          onClick={step === 'variables' ? onBack : () => {
            if (step === 'configure') setStep('variables')
            else if (step === 'metadata') setStep('configure')
          }}
          className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-400 hover:text-neutral-600 transition-colors"
        >
          <ArrowLeft size={16} />
        </button>
        <h2 className="text-sm font-bold text-neutral-900 dark:text-neutral-100 flex-1">
          {t('createTemplate.title')}
        </h2>
      </div>

      {/* Step indicator */}
      {step !== 'success' && (
        <div className="flex items-center justify-center gap-1.5 py-2 px-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-1.5">
              <div
                className={`w-2 h-2 rounded-full transition-colors ${
                  i <= stepIndex
                    ? 'bg-primary-500'
                    : 'bg-neutral-200 dark:bg-neutral-700'
                }`}
              />
              {i < 2 && (
                <div
                  className={`w-6 h-0.5 transition-colors ${
                    i < stepIndex
                      ? 'bg-primary-500'
                      : 'bg-neutral-200 dark:bg-neutral-700'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Step content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {step === 'variables' && (
          <StepVariables
            detectedNames={detectedNames}
            newVarName={newVarName}
            onNewVarNameChange={setNewVarName}
            insertError={insertError}
            isInserting={isInserting}
            isScanning={isScanning}
            onInsert={handleInsert}
            onScan={handleScan}
            onNext={() => setStep('configure')}
          />
        )}

        {step === 'configure' && (
          <StepConfigure
            detectedNames={detectedNames}
            variableConfigs={variableConfigs}
            onConfigChange={handleConfigChange}
            onNext={() => setStep('metadata')}
          />
        )}

        {step === 'metadata' && (
          <StepMetadata
            name={name}
            description={description}
            category={category}
            onNameChange={setName}
            onDescriptionChange={setDescription}
            onCategoryChange={setCategory}
            uploadPhase={uploadPhase}
            uploadError={uploadError}
            isReading={isReading}
            isPending={createTemplate.isPending}
            onCreate={handleCreate}
          />
        )}

        {step === 'success' && (
          <StepSuccess
            createdVariables={createdVariables}
            variableConfigs={variableConfigs}
            createdTemplateId={createdTemplateId}
            onBack={onBack}
          />
        )}
      </div>
    </div>
  )
}

// ============================================================
// Step 1: Insert & Scan Variables
// ============================================================

function StepVariables({
  detectedNames,
  newVarName,
  onNewVarNameChange,
  insertError,
  isInserting,
  isScanning,
  onInsert,
  onScan,
  onNext,
}: {
  detectedNames: string[]
  newVarName: string
  onNewVarNameChange: (v: string) => void
  insertError: string | null
  isInserting: boolean
  isScanning: boolean
  onInsert: () => void
  onScan: () => void
  onNext: () => void
}) {
  const { t } = useTranslation()
  const sanitized = sanitizeVariableName(newVarName)

  return (
    <div className="flex flex-col flex-1 px-3 py-2 overflow-hidden">
      <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mb-2">
        {t('wizard.stepVariablesHint')}
      </p>

      {/* Insert variable */}
      <div className="space-y-1 mb-3">
        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
          {t('wizard.insertLabel')}
        </label>
        <div className="flex gap-1.5">
          <div className="relative flex-1">
            <input
              type="text"
              value={newVarName}
              onChange={(e) => onNewVarNameChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sanitized && onInsert()}
              placeholder={t('wizard.insertPlaceholder')}
              className="w-full pl-2.5 pr-2.5 py-2 text-xs rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
            />
            {newVarName && sanitized !== newVarName.toLowerCase() && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-neutral-400 font-mono">
                {sanitized}
              </span>
            )}
          </div>
          <button
            onClick={onInsert}
            disabled={!sanitized || isInserting}
            className="shrink-0 px-2.5 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
            title={t('wizard.insertButton')}
          >
            <Plus size={16} />
          </button>
        </div>
        {insertError && (
          <p className="text-[10px] text-error-500">{insertError}</p>
        )}
      </div>

      {/* Scan button */}
      <button
        onClick={onScan}
        disabled={isScanning}
        className="flex items-center justify-center gap-1.5 w-full px-3 py-2 mb-3 text-xs rounded-lg border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-300 transition-colors disabled:opacity-50"
      >
        <Search size={13} />
        {isScanning ? t('wizard.scanning') : t('wizard.scanButton')}
      </button>

      {/* Detected variables */}
      <div className="flex-1 overflow-y-auto">
        {detectedNames.length > 0 ? (
          <>
            <p className="text-[10px] font-medium text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-1.5">
              {t('wizard.detectedVariables', { count: detectedNames.length })}
            </p>
            <div className="flex flex-wrap gap-1">
              {detectedNames.map((n) => (
                <span
                  key={n}
                  className="px-2 py-1 text-[10px] font-mono rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
                >
                  {`{{${n}}}`}
                </span>
              ))}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle size={24} className="text-neutral-300 dark:text-neutral-600 mb-2" />
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {t('wizard.noVariables')}
            </p>
            <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-1">
              {t('wizard.noVariablesHint')}
            </p>
          </div>
        )}
      </div>

      {/* Next button */}
      <button
        onClick={onNext}
        disabled={detectedNames.length === 0}
        className="mt-2 w-full px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
      >
        {t('wizard.nextConfigure')}
      </button>
    </div>
  )
}

// ============================================================
// Step 2: Configure Variables
// ============================================================

function StepConfigure({
  detectedNames,
  variableConfigs,
  onConfigChange,
  onNext,
}: {
  detectedNames: string[]
  variableConfigs: Record<string, LocalVariableConfig>
  onConfigChange: (name: string, config: LocalVariableConfig) => void
  onNext: () => void
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col flex-1 px-3 py-2 overflow-hidden">
      <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mb-2">
        {t('wizard.configureHint')}
      </p>

      <div className="flex-1 overflow-y-auto space-y-2 pb-2">
        {detectedNames.map((name) => (
          <VariableConfigCard
            key={name}
            name={name}
            config={variableConfigs[name] || { label: toTitleCase(name), field_type: 'text', is_required: true }}
            onChange={(config) => onConfigChange(name, config)}
          />
        ))}
      </div>

      <button
        onClick={onNext}
        className="mt-2 w-full px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
      >
        {t('wizard.nextDetails')}
      </button>
    </div>
  )
}

// ============================================================
// Step 3: Template Metadata
// ============================================================

function StepMetadata({
  name,
  description,
  category,
  onNameChange,
  onDescriptionChange,
  onCategoryChange,
  uploadPhase,
  uploadError,
  isReading,
  isPending,
  onCreate,
}: {
  name: string
  description: string
  category: TemplateCategory | ''
  onNameChange: (v: string) => void
  onDescriptionChange: (v: string) => void
  onCategoryChange: (v: TemplateCategory | '') => void
  uploadPhase: string | null
  uploadError: string | null
  isReading: boolean
  isPending: boolean
  onCreate: () => void
}) {
  const { t } = useTranslation()
  const isUploading = isReading || isPending || !!uploadPhase
  const canCreate = name.trim().length > 0 && !isUploading

  return (
    <div className="flex flex-col flex-1 px-3 py-2">
      {/* Template name */}
      <div className="mb-3">
        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1.5 block">
          {t('createTemplate.templateName')}
        </label>
        <input
          type="text"
          placeholder={t('createTemplate.templateNamePlaceholder')}
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          className="w-full px-2 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
        />
      </div>

      {/* Description */}
      <div className="mb-3">
        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1.5 block">
          {t('createTemplate.templateDescription')}
        </label>
        <textarea
          placeholder={t('createTemplate.templateDescriptionPlaceholder')}
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          rows={2}
          className="w-full px-2 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1 resize-none"
        />
      </div>

      {/* Category */}
      <div className="mb-4">
        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1.5 block">
          {t('templates.category')}
        </label>
        <select
          value={category}
          onChange={(e) => onCategoryChange(e.target.value as TemplateCategory | '')}
          className="w-full px-2 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
        >
          <option value="">{t('createTemplate.noCategory')}</option>
          {CATEGORIES.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {t(cat.labelKey)}
            </option>
          ))}
        </select>
      </div>

      {/* Error */}
      {uploadError && (
        <p className="text-xs text-error-500 mb-3">{uploadError}</p>
      )}

      {/* Create button */}
      <button
        onClick={onCreate}
        disabled={!canCreate}
        className="mt-auto flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
      >
        {uploadPhase ? (
          <>{uploadPhase}</>
        ) : (
          <>
            <Upload size={14} />
            {t('createTemplate.createButton')}
          </>
        )}
      </button>
    </div>
  )
}

// ============================================================
// Step 4: Success
// ============================================================

function StepSuccess({
  createdVariables,
  variableConfigs,
  createdTemplateId,
  onBack,
}: {
  createdVariables: TemplateVariable[]
  variableConfigs: Record<string, LocalVariableConfig>
  createdTemplateId: number | null
  onBack: () => void
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col items-center justify-center flex-1 px-6 py-8 text-center">
      <CheckCircle size={40} className="text-success-500 mb-4" />
      <h2 className="text-sm font-bold text-neutral-900 dark:text-neutral-100 mb-1">
        {t('wizard.success')}
      </h2>
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        {t('wizard.successVariables', { count: createdVariables.length })}
      </p>

      {createdVariables.length > 0 && (
        <div className="mt-3 w-full space-y-1">
          {createdVariables.map((v) => {
            const config = variableConfigs[v.name]
            return (
              <div
                key={v.id}
                className="flex items-center justify-between px-2.5 py-1.5 rounded-md bg-neutral-50 dark:bg-neutral-800 text-xs"
              >
                <span className="text-neutral-900 dark:text-neutral-100 font-medium truncate">
                  {config?.label || v.label}
                </span>
                <span className="text-[10px] text-neutral-400 dark:text-neutral-500 shrink-0 ml-2">
                  {config?.field_type || v.field_type}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {createdTemplateId && (
        <a
          href={`${MAIN_APP_URL}/templates/${createdTemplateId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
        >
          <ExternalLink size={12} />
          {t('wizard.editInKobSign')}
        </a>
      )}

      <button
        onClick={onBack}
        className="mt-4 text-xs text-primary-600 dark:text-primary-400 hover:underline"
      >
        {t('common.back')}
      </button>
    </div>
  )
}
